import { useEffect, useId, useRef, useState } from 'react';
import type { DesignSite } from '@freedom/shared';
import type { PtPCapacityBreakdown } from './ptp-capacity';
import { formatCapacityMbps } from './ptp-capacity';

export function PtPCapacityTrack({ breakdown, endpoints, blocked, compact = false, focusable = true }: {
  breakdown: PtPCapacityBreakdown | null;
  endpoints: readonly [DesignSite, DesignSite];
  blocked: boolean;
  compact?: boolean;
  focusable?: boolean;
}): React.JSX.Element {
  const tooltipId = useId();
  const rootRef = useRef<HTMLSpanElement>(null);
  const [pinned, setPinned] = useState(false);
  const available = !blocked && breakdown !== null;
  const denominator = available ? Math.max(1, breakdown.totalMbps, breakdown.maximumMbps ?? 0) : 1;
  const downlinkPercent = available ? Math.min(100, breakdown.downlinkMbps / denominator * 100) : 0;
  const uplinkPercent = available ? Math.min(100 - downlinkPercent, breakdown.uplinkMbps / denominator * 100) : 0;
  const downlinkLabel = `${endpoints[0].name} → ${endpoints[1].name}`;
  const uplinkLabel = `${endpoints[1].name} → ${endpoints[0].name}`;
  const maximumLabel = available && breakdown.maximumMbps !== null ? `до ${formatCapacityMbps(breakdown.maximumMbps)}` : '—';
  const ariaLabel = blocked
    ? 'Линк перекрыт; пропускная способность недоступна'
    : available
      ? `Всего ${formatCapacityMbps(breakdown.totalMbps)} мегабит в секунду; ${downlinkLabel}: ${formatCapacityMbps(breakdown.downlinkMbps)}; ${uplinkLabel}: ${formatCapacityMbps(breakdown.uplinkMbps)}; максимум ${maximumLabel}`
      : 'Пропускная способность рассчитывается';

  useEffect(() => {
    if (!pinned) return undefined;
    const closeOutside = (event: PointerEvent): void => {
      if (!rootRef.current?.contains(event.target as Node)) setPinned(false);
    };
    const closeOnEscape = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') setPinned(false);
    };
    document.addEventListener('pointerdown', closeOutside, true);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('pointerdown', closeOutside, true);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [pinned]);

  useEffect(() => setPinned(false), [blocked, breakdown?.downlinkMbps, breakdown?.uplinkMbps, breakdown?.maximumMbps]);

  return <span
    ref={rootRef}
    className={`capacity-interactive${compact ? ' compact' : ''}${pinned ? ' open' : ''}`}
    role={focusable ? 'button' : undefined}
    aria-label={focusable ? ariaLabel : undefined}
    aria-describedby={focusable ? tooltipId : undefined}
    aria-disabled={focusable && blocked ? true : undefined}
    aria-busy={focusable && !blocked && !breakdown ? true : undefined}
    tabIndex={focusable ? 0 : undefined}
    onClick={focusable ? () => setPinned((current) => !current) : undefined}
    onKeyDown={focusable ? (event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        setPinned((current) => !current);
      } else if (event.key === 'Escape') {
        setPinned(false);
      }
    } : undefined}
  >
    <span className="capacity-track" aria-hidden="true">
      <i className="capacity-segment downlink" style={{ width: `${downlinkPercent}%` }} />
      <i className="capacity-segment uplink" style={{ width: `${uplinkPercent}%` }} />
    </span>
    <span id={tooltipId} className={`capacity-tooltip${blocked ? ' blocked' : ''}`} role="tooltip">
      {available ? <>
        <span className="capacity-tooltip-row"><span title={downlinkLabel}><i className="downlink" />{downlinkLabel}</span><strong>{formatCapacityMbps(breakdown.downlinkMbps)} Мбит/с</strong></span>
        <span className="capacity-tooltip-row"><span title={uplinkLabel}><i className="uplink" />{uplinkLabel}</span><strong>{formatCapacityMbps(breakdown.uplinkMbps)} Мбит/с</strong></span>
        <span className="capacity-tooltip-row total"><span>Всего / максимум</span><strong>{formatCapacityMbps(breakdown.totalMbps)} / {maximumLabel} Мбит/с</strong></span>
      </> : <span className="capacity-tooltip-row total"><span>Пропускная способность</span><strong>{blocked ? 'Линк перекрыт' : 'Расчёт…'}</strong></span>}
    </span>
  </span>;
}
